// Landing.js - Code source complet
import React, { useEffect, useState } from 'react';
import { ArrowRight, Code, Zap, Shield, Clock, Users, Server, Box, Wrench, PenTool } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Composant ServiceCard amélioré
const ServiceCard = ({ icon: Icon, title, description, features, cta, ctaLink }) => {
  const navigate = useNavigate();
  
  return (
    <div className="group">
      <div className="relative bg-black/40 backdrop-blur-sm rounded-xl p-8 border border-white/10 
                    hover:border-green-500/50 transition-all duration-300">
        {/* Effet de brillance */}
        <div className="absolute -inset-[1px] bg-gradient-to-r from-green-500 to-[#017D7E] rounded-xl opacity-0 
                      group-hover:opacity-20 blur-sm transition-opacity duration-300" />
        
        <div className="relative z-10">
          <div className="p-3 rounded-lg bg-[#017D7E]/10 w-fit mb-6 transform group-hover:scale-110 
                       transition-transform duration-300">
            <Icon className="w-6 h-6 text-[#017D7E]" />
          </div>
          
          <h3 className="text-2xl font-bold mb-4 text-white group-hover:text-[#017D7E] transition-colors">
            {title}
          </h3>
          
          <p className="text-white/70 mb-6">
            {description}
          </p>
          
          <ul className="space-y-3 mb-8">
            {features.map((feature, index) => (
              <li key={index} className="flex items-center gap-2 text-white/70">
                <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
                {feature}
              </li>
            ))}
          </ul>
          
          <button
            onClick={() => navigate(ctaLink)}
            className="w-full py-3 rounded-lg border border-green-500/50 text-white font-medium
                     hover:bg-[#017D7E]/10 transition-all relative overflow-hidden group">
            <span className="relative z-10 flex items-center justify-center gap-2">
              {cta}
              <ArrowRight className="w-5 h-5 transform group-hover:translate-x-1 transition-transform" />
            </span>
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent
                          translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Composant FeatureCard amélioré
const FeatureCard = ({ icon: Icon, title, description }) => (
  <div className="group">
    <div className="relative bg-black/40 backdrop-blur-sm rounded-xl p-8 border border-white/10 
                  hover:border-green-500/50 transition-all duration-300 h-full">
      {/* Effet de brillance */}
      <div className="absolute -inset-[1px] bg-gradient-to-r from-green-500 to-[#017D7E] rounded-xl opacity-0 
                    group-hover:opacity-20 blur-sm transition-opacity duration-300" />
      
      <div className="relative z-10">
        <div className="w-16 h-16 rounded-xl bg-[#017D7E]/10 flex items-center justify-center mx-auto mb-6
                      transform group-hover:scale-110 group-hover:bg-[#017D7E]/20 transition-all duration-300">
          <Icon className="w-8 h-8 text-[#017D7E]" />
        </div>
        
        <h3 className="text-xl font-bold mb-4 text-center group-hover:text-[#017D7E] transition-colors">
          {title}
        </h3>
        
        <p className="text-white/70 text-center">
          {description}
        </p>
      </div>
    </div>
  </div>
);

// Composant ParticlesBackground
const ParticlesBackground = () => (
  <div className="fixed inset-0 z-0 overflow-hidden">
    {[...Array(50)].map((_, i) => (
      <div
        key={i}
        className="absolute rounded-full bg-[#017D7E]/20"
        style={{
          width: Math.random() * 4 + 'px',
          height: Math.random() * 4 + 'px',
          top: Math.random() * 100 + '%',
          left: Math.random() * 100 + '%',
          animation: `float ${Math.random() * 6 + 4}s ease-in-out infinite`,
          animationDelay: `${Math.random() * 4}s`
        }}
      />
    ))}
  </div>
);

// Composant WaveTransition
const WaveTransition = () => (
  <div className="absolute bottom-0 left-0 right-0 h-24 overflow-hidden">
    <svg 
      viewBox="0 0 1200 120" 
      preserveAspectRatio="none"
      className="absolute bottom-0 w-full h-full text-current"
      style={{ transform: 'rotate(180deg)' }}
    >
      <path
        d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z"
        opacity=".25"
        className="fill-current"
      />
      <path
        d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z"
        opacity=".5"
        className="fill-current"
      />
      <path
        d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z"
        className="fill-current"
      />
    </svg>
  </div>
);

// Styles CSS pour les animations
const styles = `
@keyframes float {
  0% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
  100% { transform: translateY(0px); }
}

@keyframes pulse {
  0% { transform: scale(1); opacity: 0.5; }
  50% { transform: scale(1.05); opacity: 0.8; }
  100% { transform: scale(1); opacity: 0.5; }
}

@keyframes gradientBG {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

.gradient-border {
  background: linear-gradient(60deg, #f79533, #f37055, #ef4e7b, #a166ab, #5073b8, #1098ad, #07b39b, #6fba82);
  background-size: 300% 300%;
  animation: gradientBG 4s ease infinite;
}

.floating {
  animation: float 6s ease-in-out infinite;
}

.pulse {
  animation: pulse 2s ease-in-out infinite;
}

.text-glow {
  text-shadow: 0 0 10px rgba(59, 130, 246, 0.5);
}
`;

// Composant principal Landing
const Landing = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState({});

  // Injection des styles
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = styles;
    document.head.appendChild(styleSheet);
    return () => styleSheet.remove();
  }, []);

  // Hook pour l'effet Parallax
  useEffect(() => {
    const handleMouseMove = (e) => {
      const parallaxElements = document.querySelectorAll('.parallax');
      parallaxElements.forEach(element => {
        const speed = element.getAttribute('data-speed') || 0.2;
        const x = (window.innerWidth - e.pageX * speed) / 100;
        const y = (window.innerHeight - e.pageY * speed) / 100;
        element.style.transform = `translateX(${x}px) translateY(${y}px)`;
      });
    };

    document.addEventListener('mousemove', handleMouseMove);
    return () => document.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Observer pour les animations au scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          setIsVisible(prev => ({
            ...prev,
            [entry.target.id]: entry.isIntersecting
          }));
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.animate-on-scroll').forEach(
      el => observer.observe(el)
    );

    return () => observer.disconnect();
  }, []);

  // Données des services
  const services = [
    {
      icon: Box,
      title: "Solutions Prêtes à l'Emploi",
      description: "Un catalogue de solutions éprouvées, immédiatement disponibles en sandbox pour évaluation",
      features: [
        "Déploiement rapide",
        "Produits testés et validés",
        "Documentation complète",
        "Support dédié"
      ],
      cta: "Découvrir le Catalogue",
      ctaLink: "/portfolio"
    },
    {
      icon: Wrench,
      title: "Adaptation & Intégration",
      description: "Personnalisation de nos solutions pour répondre précisément à vos besoins spécifiques",
      features: [
        "Configuration sur mesure",
        "Intégration à votre SI",
        "Formation des équipes",
        "Accompagnement projet"
      ],
      cta: "Explorer notre Approche",
      ctaLink: "/integration"
    },
    {
      icon: PenTool,
      title: "Développement Factory",
      description: "Création de solutions sur mesure selon notre méthodologie industrielle",
      features: [
        "Co-conception avec vos experts",
        "Composants métier certifiés",
        "Conformité native",
        "Maintenance évolutive"
      ],
      cta: "Notre Approche Factory",
      ctaLink: "/factory"
    }
  ];

  // Données des features
  const features = [
    {
      icon: Code,
      title: "Composants Certifiés",
      description: "Bibliothèque de composants métier réduisant les délais de 60%"
    },
    {
      icon: Shield,
      title: "Conformité Native",
      description: "Respect des exigences réglementaires dès la conception"
    },
    {
      icon: Zap,
      title: "Déploiement Rapide",
      description: "Time-to-market optimisé grâce à notre méthodologie"
    },
    {
      icon: Clock,
      title: "ROI Accéléré",
      description: "Retour sur investissement visible dès 6 mois"
    }
  ];

  return (
    <div className="min-h-screen text-white bg-[#050A1C] relative overflow-hidden">
      {/* Fond avec particules et effets */}
      <ParticlesBackground />

      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(1,125,126,0.15),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center_left,rgba(0,169,157,0.1),transparent_50%)]" />
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-green-500/50 to-transparent" />
      </div>

      {/* Grille d'arrière-plan */}
      <div className="fixed inset-0 z-0 opacity-30">
        <div className="absolute inset-0" 
          style={{
            backgroundImage: 'linear-gradient(to right, rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '4rem 4rem',
            maskImage: 'radial-gradient(circle at 50% 50%, black, transparent 80%)'
          }} />
      </div>

      {/* Contenu principal */}
      <div className="relative z-10">
        {/* Hero Section */}
        <section className="min-h-[90vh] flex items-center justify-center relative overflow-hidden">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              {/* Badge animé */}
              {/* Suite du composant Landing */}
              <div className="inline-block px-4 py-2 rounded-full bg-gradient-to-r from-green-500/20 to-[#017D7E]/20 text-[#017D7E] mb-8 
                            relative group hover:scale-105 transition-transform duration-300 cursor-default">
                <div className="relative z-10 flex items-center gap-2">
                  Solutions Technologiques pour la Finance
                </div>
                <div className="absolute inset-0 bg-gradient-to-r from-green-500/10 to-[#017D7E]/10 rounded-full animate-pulse" />
              </div>

              {/* Titre principal */}
              <h1 className="text-4xl md:text-7xl font-bold mb-8 leading-tight animate-on-scroll"
                  id="hero-title"
                  style={{
                    opacity: isVisible['hero-title'] ? 1 : 0,
                    transform: isVisible['hero-title'] ? 'translateY(0)' : 'translateY(2rem)',
                    transition: 'all 1000ms'
                  }}>
                Choisissez
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#017D7E] to-[#00A99D]
                              relative after:absolute after:bottom-0 after:left-0 after:w-full after:h-[1px]
                              after:bg-gradient-to-r after:from-[#017D7E] after:to-[#00A99D]">
                  Votre Mode d'Accompagnement
                </span>
              </h1>

              {/* Description */}
              <p className="text-lg md:text-xl text-white/70 mb-12 parallax" data-speed="0.1">
                De la solution prête à l'emploi au développement sur mesure, 
                nous nous adaptons à vos besoins avec notre approche industrielle éprouvée.
              </p>

              {/* Bouton CTA principal */}
              <button
                onClick={() => navigate('/contact')}
                className="group relative px-6 md:px-8 py-3 md:py-4 rounded-lg bg-gradient-to-r from-green-500 to-[#017D7E]
                          hover:shadow-lg hover:shadow-green-500/20 text-white font-medium transition-all duration-300
                          hover:scale-105">
                <span className="relative z-10 flex items-center gap-2">
                  Échanger avec nos Experts
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-green-600 to-emerald-600 rounded-lg opacity-0
                              transition-opacity duration-300 group-hover:opacity-100" />
              </button>
            </div>
          </div>

          {/* Effet de transition en bas */}
          <WaveTransition />
        </section>

        {/* Section Services */}
        <section className="py-24 relative">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              {/* Titre de section */}
              <div className="text-center mb-16 animate-on-scroll" id="services-title">
                <h2 className="text-3xl md:text-4xl font-bold mb-6"
                    style={{
                      opacity: isVisible['services-title'] ? 1 : 0,
                      transition: 'opacity 1000ms'
                    }}>
                  Nos Modes d'Accompagnement
                </h2>
                <p className="text-lg md:text-xl text-white/70">
                  Trois approches adaptées à vos enjeux et contraintes
                </p>
              </div>

              {/* Grille de services */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {services.map((service, index) => (
                  <div key={index}
                       className="animate-on-scroll"
                       id={`service-${index}`}
                       style={{
                         opacity: isVisible[`service-${index}`] ? 1 : 0,
                         transform: isVisible[`service-${index}`] ? 'translateY(0)' : 'translateY(2rem)',
                         transition: `all 1000ms ${index * 200}ms`
                       }}>
                    <ServiceCard {...service} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Section Features */}
        <section className="py-24 relative">
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-r from-green-500/10 to-transparent opacity-20"
                 style={{ transform: 'skewY(-12deg)' }} />
          </div>

          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-6xl mx-auto">
              {/* Titre de section */}
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  L'Excellence
                  <span className="relative text-transparent bg-clip-text bg-gradient-to-r from-[#017D7E] to-[#00A99D]">
                    {" "}Factory
                  </span>
                </h2>
                <p className="text-lg md:text-xl text-white/70">
                  Une approche industrielle garantissant qualité et performance
                </p>
              </div>

              {/* Grille de features */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8">
                {features.map((feature, index) => (
                  <div key={index}
                       className="animate-on-scroll"
                       id={`feature-${index}`}
                       style={{
                         opacity: isVisible[`feature-${index}`] ? 1 : 0,
                         transform: isVisible[`feature-${index}`] ? 'translateY(0)' : 'translateY(2rem)',
                         transition: `all 1000ms ${index * 200}ms`
                       }}>
                    <FeatureCard {...feature} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Section CTA finale */}
        <section className="py-24 relative">
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-t from-green-500/10 to-transparent opacity-20" />
          </div>

          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 animate-on-scroll"
                  id="cta-title"
                  style={{
                    opacity: isVisible['cta-title'] ? 1 : 0,
                    transform: isVisible['cta-title'] ? 'translateY(0)' : 'translateY(2rem)',
                  }}>
                Lancez Votre Projet dès Maintenant
              </h2>
              <p className="text-lg md:text-xl text-white/70 mb-12">
                Nos experts sont à votre disposition pour échanger sur vos besoins
                et vous accompagner dans le choix de la meilleure approche.
              </p>

              <div className="flex flex-col md:flex-row gap-6 justify-center">
                <button
                  onClick={() => navigate('/portfolio')}
                  className="group relative px-8 py-4 rounded-lg bg-gradient-to-r from-green-500 to-[#017D7E]
                            hover:shadow-lg hover:shadow-green-500/20 text-white font-medium transition-all duration-300
                            hover:scale-105">
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    Explorer nos Solutions
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </button>

                <button
                  onClick={() => navigate('/contact')}
                  className="px-8 py-4 rounded-lg border border-green-500/50 hover:bg-[#017D7E]/10 text-white
                           font-medium transition-all hover:border-green-400">
                  Nous Contacter
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Landing;